// ═══════════════════════════════════════════════════════════════════════════════
// ── Pure SVG Chart Components ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Chart Card wrapper ───────────────────────────────────────────────────────
export function ChartCard({ title, subtitle, children }) {
  return (
    <div className="wd-card" style={{ padding:20 }}>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>{title}</div>
        {subtitle && <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── Grouped Bar Chart (SVG) ──────────────────────────────────────────────────
export function BarChart({ data, bars, xKey, height = 220 }) {
  const W = 580, H = height;
  const padL = 40, padR = 16, padT = 16, padB = 32;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  // Compute max value
  const maxVal = Math.max(...data.flatMap(d =>
    bars.map(b => b.computed ? b.key(d) : (d[b.key] || 0))
  )) * 1.15;

  const groupW = chartW / data.length;
  const barW   = Math.min(groupW / (bars.length + 1), 28);
  const gap    = (groupW - barW * bars.length) / 2;

  // Y gridlines
  const ticks  = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(maxVal * t));

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height }}>
        {/* Grid lines */}
        {ticks.map(tick => {
          const y = padT + chartH - (tick / maxVal) * chartH;
          return (
            <g key={tick}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--wd-border)" strokeWidth="1" strokeDasharray="3,3" />
              <text x={padL - 4} y={y + 4} fontSize="9" fill="var(--wd-t3)" textAnchor="end">{tick}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, gi) => {
          const groupX = padL + gi * groupW;
          return (
            <g key={d[xKey]}>
              {bars.map((bar, bi) => {
                const val  = bar.computed ? bar.key(d) : (d[bar.key] || 0);
                const barH = (val / maxVal) * chartH;
                const x    = groupX + gap + bi * barW;
                const y    = padT + chartH - barH;
                return (
                  <g key={bi}>
                    <rect x={x} y={y} width={barW - 2} height={barH}
                      fill={bar.color} rx="3" opacity=".9">
                      <title>{bar.label}: {val}</title>
                    </rect>
                  </g>
                );
              })}
              {/* X label */}
              <text x={groupX + groupW / 2} y={H - 8} fontSize="10" fill="var(--wd-t3)" textAnchor="middle">{d[xKey]}</text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div style={{ display:"flex", gap:16, justifyContent:"center", marginTop:4 }}>
        {bars.map(b => (
          <div key={b.label} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:b.color }} />
            <span style={{ fontSize:11, color:"var(--wd-t3)" }}>{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Line Chart (SVG) ─────────────────────────────────────────────────────────
export function LineChart({ data, dataKey, xKey, color, height = 200, formatY, target, targetLabel }) {
  const W = 540, H = height;
  const padL = 48, padR = 16, padT = 20, padB = 30;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const values = data.map(d => d[dataKey]);
  const maxVal = Math.max(...values) * 1.2;
  const minVal = 0;

  const toX = (i) => padL + (i / (data.length - 1)) * chartW;
  const toY = (v) => padT + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

  const pts = data.map((d, i) => `${toX(i)},${toY(d[dataKey])}`).join(" ");
  const area = `${padL},${padT + chartH} ${pts} ${toX(data.length - 1)},${padT + chartH}`;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(maxVal * t));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height }}>
      <defs>
        <linearGradient id={`lg-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid */}
      {ticks.map(tick => {
        const y = toY(tick);
        return (
          <g key={tick}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--wd-border)" strokeWidth="1" strokeDasharray="3,3" />
            <text x={padL - 4} y={y + 4} fontSize="9" fill="var(--wd-t3)" textAnchor="end">
              {formatY ? formatY(tick) : tick}
            </text>
          </g>
        );
      })}

      {/* Target line */}
      {target !== undefined && (
        <>
          <line x1={padL} y1={toY(target)} x2={W - padR} y2={toY(target)}
            stroke="#22c55e" strokeWidth="1.5" strokeDasharray="6,3" opacity=".7" />
          <text x={W - padR - 2} y={toY(target) - 4} fontSize="9" fill="#22c55e" textAnchor="end">{targetLabel}</text>
        </>
      )}

      {/* Area */}
      <polygon points={area} fill={`url(#lg-${dataKey})`} />

      {/* Line */}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

      {/* Dots + tooltips */}
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={toX(i)} cy={toY(d[dataKey])} r="4" fill={color} stroke="var(--wd-card)" strokeWidth="2">
            <title>{d[xKey]}: {formatY ? formatY(d[dataKey]) : d[dataKey]}</title>
          </circle>
          <text x={toX(i)} y={H - 8} fontSize="10" fill="var(--wd-t3)" textAnchor="middle">{d[xKey]}</text>
        </g>
      ))}
    </svg>
  );
}

// ─── Donut Chart (SVG) ────────────────────────────────────────────────────────
export function DonutChart({ data, total, valueKey, labelKey, unit = "" }) {
  const R = 60, cx = 90, cy = 90;
  const circumference = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div style={{ display:"flex", alignItems:"center", gap:20, width:"100%", minWidth:0 }}>
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ flexShrink:0 }}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--wd-border-md)" strokeWidth="20" />
        {/* Segments */}
        {data.map((d, i) => {
          const pct  = d[valueKey] / total;
          const dash = pct * circumference;
          const seg = (
            <circle key={i} cx={cx} cy={cy} r={R} fill="none"
              stroke={d.color} strokeWidth="20"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset * circumference + circumference / 4}
              strokeLinecap="butt"
              style={{ transition:"stroke-dasharray .4s" }}
            >
              <title>{d[labelKey]}: {d[valueKey]}{unit}</title>
            </circle>
          );
          offset += pct;
          return seg;
        })}
        {/* Center label */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="22" fontWeight="800" fill="var(--wd-t1)">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="var(--wd-t3)">total</text>
      </svg>

      {/* Legend */}
      <div style={{ display:"flex", flexDirection:"column", gap:7, flex:1, minWidth:0 }}>
        {data.map(d => (
          <div key={d[labelKey]} style={{ display:"flex", alignItems:"flex-start", gap:7, minWidth:0 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:d.color, flexShrink:0 }} />
            <span
              style={{
                fontSize:11,
                color:"var(--wd-t2)",
                flex:1,
                minWidth:0,
                lineHeight:1.3,
                wordBreak:"break-word",
                overflow:"hidden",
                textOverflow:"ellipsis",
                display:"-webkit-box",
                WebkitLineClamp:2,
                WebkitBoxOrient:"vertical",
              }}
            >
              {d[labelKey]}
            </span>
            <span style={{ fontSize:12, fontWeight:700, color:"var(--wd-t1)", fontFamily:"var(--wd-mono)" }}>
              {d[valueKey]}{unit}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Horizontal Bar (compact) ─────────────────────────────────────────────────
export function HorizontalBar({ data, valueKey, labelKey }) {
  const max = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10, paddingTop:4 }}>
      {data.map(d => (
        <div key={d[labelKey]}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
            <span style={{ fontSize:12, color:"var(--wd-t2)" }}>{d[labelKey]}</span>
            <span style={{ fontSize:12, fontWeight:700, color:"var(--wd-t1)", fontFamily:"var(--wd-mono)" }}>{d[valueKey]}</span>
          </div>
          <div style={{ height:6, borderRadius:3, background:"var(--wd-hover)" }}>
            <div style={{ height:"100%", borderRadius:3, width:`${(d[valueKey]/max)*100}%`, background: d.color, transition:"width .4s" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Horizontal Bar Full-width (for leaderboards) ─────────────────────────────
export function HorizontalBarFull({ data, max }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12, paddingTop:4 }}>
      {data.map((d, i) => (
        <div key={d.label} style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:22, height:22, borderRadius:"50%", background:"var(--wd-hover)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"var(--wd-t3)", flexShrink:0 }}>{i+1}</div>
          <span style={{ fontSize:12, color:"var(--wd-t2)", width:110, flexShrink:0, lineHeight:1.3 }}>{d.label}</span>
          <div style={{ flex:1, height:8, borderRadius:4, background:"var(--wd-hover)" }}>
            <div style={{ height:"100%", borderRadius:4, width:`${(d.value/max)*100}%`, background:d.color, transition:"width .5s" }} />
          </div>
          <span style={{ fontSize:12, fontWeight:700, color:"var(--wd-t1)", width:36, textAlign:"right", fontFamily:"var(--wd-mono)" }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Rating Bar ───────────────────────────────────────────────────────────────
export function RatingBar({ rating }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <div style={{ display:"flex", gap:1 }}>
        {[1,2,3,4,5].map(i => (
          <svg key={i} width="10" height="10" viewBox="0 0 24 24"
            fill={i <= Math.round(rating) ? "var(--wd-yellow)" : "var(--wd-border-md)"}
            stroke="none">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        ))}
      </div>
      <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:500 }}>{rating}</span>
    </div>
  );
}

export function _cellAddr(row, col) {
  // Convert 0-based row/col to A1 notation
  let colStr = "";
  let c = col;
  do {
    colStr = String.fromCharCode(65 + (c % 26)) + colStr;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return `${colStr}${row + 1}`;
}
 
export function _styleCell(ws, row, col, style) {
  const addr = _cellAddr(row, col);
  if (!ws[addr]) ws[addr] = { t: "z", v: "" };
  ws[addr].s = style;
}
 
export function _styleRange(ws, r1, c1, r2, c2, style) {
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      _styleCell(ws, r, c, style);
    }
  }
}

export function getFinalStatusEmoji(status = "") {
  const s = status.toUpperCase();
  if (s.includes("SETTLED"))    return "✅ SETTLED";
  if (s.includes("CRITICAL"))   return "🔴 CRITICAL";
  if (s.includes("OVERDUE"))    return "🟡 OVERDUE";
  return "🔵 IN PROGRESS";
}